import { motion } from 'framer-motion';
import { Play, Film, Tv, Eye } from 'lucide-react';
import type { CurrentlyWatching } from '../../../services/traktApi';
import { LABELS } from '../../../data/labels';

export const NowWatchingCard = ({ watching, loading, labels }: {
  watching: CurrentlyWatching | null;
  loading: boolean;
  labels: typeof LABELS['fr'];
}) => {
  if (loading) {
    return (
      <div className="card card-hover p-6 h-full">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const getTitle = () => {
    if (!watching) return null;
    if (watching.type === 'movie' && watching.movie) {
      return watching.movie.title;
    }
    if (watching.type === 'episode' && watching.show && watching.episode) {
      return `${watching.show.title} - S${watching.episode.season}E${watching.episode.number}`;
    }
    return null;
  };

  const getPoster = () => {
    if (!watching) return null;
    if (watching.type === 'movie' && watching.movie) {
      return watching.movie.poster || null;
    }
    if (watching.type === 'episode' && watching.show) {
      return watching.show.poster || null;
    }
    return null;
  };

  const getTraktUrl = () => {
    if (!watching) return '#';
    if (watching.type === 'movie' && watching.movie) {
      return `https://trakt.tv/movies/${watching.movie.ids.slug}`;
    }
    if (watching.type === 'episode' && watching.show && watching.episode) {
      return `https://trakt.tv/shows/${watching.show.ids.slug}/seasons/${watching.episode.season}/episodes/${watching.episode.number}`;
    }
    return '#';
  };

  const title = getTitle();
  const posterUrl = getPoster();
  const traktUrl = getTraktUrl();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="card card-hover p-6 h-full"
    >
      <div className="section-title mb-4">
        <Play className="w-5 h-5 text-[#ed1c24]" />
        {labels.now_watching}
        {watching && (
          <span className="ml-2 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-[#ed1c24] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ed1c24]"></span>
          </span>
        )}
      </div>

      {watching && title ? (
        <a
          href={traktUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 group"
        >
          <div className="w-24 h-36 rounded-lg overflow-hidden bg-[#1a1a1a] flex-shrink-0 shadow-md">
            {posterUrl ? (
              <img
                src={posterUrl}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#666]">
                {watching.type === 'movie' ? <Film className="w-8 h-8" /> : <Tv className="w-8 h-8" />}
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#ed1c24]/10 text-[#ed1c24]">
                {watching.type === 'movie' ? labels.movies : labels.shows}
              </span>
              {watching.progress && (
                <span className="text-xs text-[#5e5e5e]">{Math.round(watching.progress)}%</span>
              )}
            </div>
            <h4 className="font-semibold text-[#1d1d1f] text-lg mb-1 group-hover:text-[#ed1c24] transition-colors">{title}</h4>
            {watching.type === 'episode' && watching.episode && (
              <p className="text-sm text-[#5e5e5e]">{watching.episode.title}</p>
            )}
            {watching.progress && (
              <div className="mt-3 progress-track">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${watching.progress}%` }}
                  className="progress-fill bg-[#ed1c24]"
                />
              </div>
            )}
          </div>
        </a>
      ) : (
        <div className="flex flex-col items-center justify-center h-32 text-[#8e8e93]">
          <Eye className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">{labels.nothing_watching}</p>
        </div>
      )}
    </motion.div>
  );
};
